import React, { useCallback, useState } from 'react';

import api from '../api';
import { useGet, useGetOne, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';
import { Voucher } from '../model';
import { GetTravelFileTotalAccountingRes } from '../helpers';

import Context, {
    AddDocumentBody,
    BalanceGroupTravelFileBody,
    CancelVoucherBody,
    EditVoucherBody,
    GetTravelFileTotalAccountingBody,
    ReconcileVouchersBody,
    ReconcileResult,
    UnreconcileVoucherBody
} from './context';

interface Props {
    children: React.ReactNode;
}

const VouchersProvider: React.FC<Props> = (props: Props) => {
    const { children } = props;
    const [data, setData] = useState<Array<Voucher>>([]);
    const [queryData, setQueryData] = useState<Array<Voucher>>([]);
    const [contextGetVouchersCount, setContextGetVouchersCount] = useState<number>(0);
    const [selected, setSelected] = useState<Voucher | null>(null);
    const [error, setError] = useState({});

    const handleGetData = useCallback((res) => {
        setData(res);
        return res;
    }, []);
    const get = useGet(api, data, handleGetData, setError);

    const getOne = useGetOne(api, setSelected, setError);

    // getById returns info as object (not array like getOne)
    const getById = useCallback(async (id: string) => {
        try {
            const result = await api.getById(id);
            setSelected(result);
            return result;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const resetContext = useCallback(() => {
        setData([]);
        setSelected(null);
        setError({});
    }, []);

    const handleSetData = useCallback((item, newData) => {
        setData(newData);
        setQueryData(newData);
    }, []);
    const post = usePost(api, data, handleSetData, setError);

    const put = usePut(api, data, handleSetData, setError);

    const handleDeleteCallback = useCallback((id, newData) => {
        setData(newData);
        setSelected(null);
        return id;
    }, []);
    const remove = useRemove(api, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(api, data, handleDeleteCallback, setError);

    const addDocument = useCallback(async (body: AddDocumentBody) => {
        try {
            const newVoucher: Voucher = await api.addDocument(body);
            setSelected(newVoucher);
            return newVoucher;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const balanceGroupTravelFile = useCallback(async (body: BalanceGroupTravelFileBody) => {
        try {
            const newVouchers: Voucher[] = await api.balanceGroupTravelFile(body);
            return newVouchers;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);
    const getTravelFilesTotalAccounting = useCallback(async (body: GetTravelFileTotalAccountingBody) => {
        try {
            const travelFileTotalAccounting: GetTravelFileTotalAccountingRes =
                await api.getTravelFilesTotalAccounting(body);
            return travelFileTotalAccounting;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const amendVoucher = useCallback(async (body: EditVoucherBody) => {
        try {
            const newVoucher: Voucher = await api.amendVoucher(body);
            setSelected(newVoucher);
            return newVoucher;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const cancelVoucher = useCallback(async (body: CancelVoucherBody) => {
        try {
            const updatedVoucher: Voucher = await api.cancelVoucher(body);
            setSelected(updatedVoucher);
            return updatedVoucher;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const reconcileDocuments = useCallback(async (body: ReconcileVouchersBody) => {
        try {
            // The endpoint returns the updated documents alongside an allocation
            // report; tolerate a plain array too, so a client running against an
            // older server keeps working.
            const response = await api.reconcileDocuments(body);
            const result: ReconcileResult = Array.isArray(response)
                ? { documents: response }
                : { ...response, documents: response?.documents || [] };
            setData(result.documents);
            setQueryData(result.documents);
            return result;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const unreconcileDocument = useCallback(async (body: UnreconcileVoucherBody) => {
        try {
            const updatedVouchers: Voucher[] = await api.unreconcileDocument(body);
            setData(updatedVouchers);
            setQueryData(updatedVouchers);
            return updatedVouchers;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const query = useCallback(async (query) => {
        try {
            const foundVouchers = await api.query(query);
            setData(foundVouchers);
            setQueryData(foundVouchers);
            return foundVouchers;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const getTurnoverDocuments = useCallback(async (query) => {
        try {
            const foundVouchers = await api.getTurnoverDocuments(query);
            setData(foundVouchers);
            setQueryData(foundVouchers);
            return foundVouchers;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const getTurnoverSummaryDocuments = useCallback(async (query) => {
        try {
            const foundVouchers = await api.getTurnoverSummaryDocuments(query);
            setData(foundVouchers);
            setQueryData(foundVouchers);
            return foundVouchers;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const markVouchersAsExported = useCallback(async (ids: Array<string | number>) => {
        try {
            return await api.markVouchersAsExported(ids);
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const enrichGlAccounts = useCallback(async (voucherIds: Array<string | number>) => {
        try {
            return await api.enrichGlAccounts(voucherIds);
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const getDocumentPostings = useCallback(async (voucherIds: Array<string | number>) => {
        try {
            return await api.getDocumentPostings(voucherIds);
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const getLedgerReversals = useCallback(
        async (body: {
            toCreatedAt: string;
            fromCreatedAt?: string;
            includeExported?: boolean;
            documentIds?: Array<string | number>;
        }) => {
            try {
                return await api.getLedgerReversals(body);
            } catch (err: any) {
                setError(err);
                throw err;
            }
        },
        []
    );

    const markLedgerReversalsExported = useCallback(async (ids: Array<string | number>) => {
        try {
            return await api.markLedgerReversalsExported(ids);
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const getLedgerHeldBack = useCallback(async (body: { toCreatedAt: string; fromCreatedAt?: string; stamp?: boolean }) => {
        try {
            return await api.getLedgerHeldBack(body);
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    return (
        <Context.Provider
            value={{
                get,
                getOne,
                getById,
                post,
                put,
                remove,
                softDelete,
                data,
                setData,
                selected,
                setSelected,
                error,
                addDocument,
                balanceGroupTravelFile,
                getTravelFilesTotalAccounting,
                amendVoucher,
                cancelVoucher,
                contextGetVouchersCount,
                setContextGetVouchersCount,
                query,
                getTurnoverDocuments,
                getTurnoverSummaryDocuments,
                queryData,
                setQueryData,
                reconcileDocuments,
                unreconcileDocument,
                resetContext,
                markVouchersAsExported,
                enrichGlAccounts,
                getDocumentPostings,
                getLedgerReversals,
                markLedgerReversalsExported,
                getLedgerHeldBack,
            }}
        >
            {children}
        </Context.Provider>
    );
};

export default VouchersProvider;
